import { ref, type Ref } from "vue";
import { Facade } from "@/drawing";
import { usePlanAnalysisState } from "@/stores/PlanAnalysisState";
import type { PlanUnit } from "@/utils/plan-analysis/types";

/**
 * Lancement d'une analyse du plan. Tout point d'entrée (bouton Analyser,
 * relance depuis l'onglet IA) passe par ici : la modale de validation
 * (`AnalysisLaunchDialog`, branchée sur `showLaunchDialog` / `suggestedUnit`
 * / `confirmLaunch`) s'affiche à CHAQUE lancement, et rien ne démarre avant
 * sa confirmation.
 *
 * L'unité du plan se règle dans cette modale, et seulement là : l'IA rédige
 * les mesures avec elle, elle doit donc être connue AVANT l'analyse. Elle
 * est présélectionnée — celle déjà choisie pour ce plan, sinon celle que
 * déclare le DWG (`$INSUNITS`, trop souvent l'unité du gabarit et pas celle
 * du dessin pour être appliquée sans confirmation).
 *
 * @param runAnalysis le pipeline complet (`usePlanAnalysis().runAnalysis`).
 */
export function useAnalysisLaunch(runAnalysis: () => Promise<boolean>): {
  showLaunchDialog: Ref<boolean>;
  suggestedUnit: Ref<PlanUnit | null>;
  requestAnalysis: () => void;
  confirmLaunch: (unit: PlanUnit) => Promise<void>;
} {
  const state = usePlanAnalysisState();
  const showLaunchDialog = ref(false);
  /** Unité présélectionnée dans la modale. */
  const suggestedUnit = ref<PlanUnit | null>(null);

  const requestAnalysis = (): void => {
    if (state.isAnalyzing) return;
    suggestedUnit.value =
      state.unit ??
      (Facade.isInitialized ? Facade.observations.readPlanUnit() : null);
    showLaunchDialog.value = true;
  };

  const confirmLaunch = async (unit: PlanUnit): Promise<void> => {
    state.setUnit(unit);
    const done = await runAnalysis();
    // Le résultat s'affiche dans l'onglet IA du volet Observations.
    if (done) state.requestOpen();
  };

  return { showLaunchDialog, suggestedUnit, requestAnalysis, confirmLaunch };
}
